import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';
import type { FriendsData, PassportTab } from '../types/passport';

interface UseFriendsDataOptions {
  effectiveToken: string | null;
  passportTab?: PassportTab;
  /** When true, fetch immediately on mount regardless of passportTab. */
  alwaysFetch?: boolean;
}

export function useFriendsData(options: UseFriendsDataOptions) {
  const { effectiveToken, passportTab, alwaysFetch } = options;

  const [friendsData, setFriendsData] = useState<FriendsData | null>(null);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  const fetchFriends = useCallback(async () => {
    if (!effectiveToken) return;
    setFriendsLoading(true);
    try {
      const { data: fd } = await axios.get(`${API_URL || ''}/api/friends`, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      setFriendsData(fd);
    } catch {
      // silently fail
    } finally {
      setFriendsLoading(false);
    }
  }, [effectiveToken]);

  useEffect(() => {
    const shouldFetch = alwaysFetch || passportTab === 'friends' || passportTab === 'challenges';
    if (shouldFetch && !friendsData && effectiveToken) {
      fetchFriends();
    }
  }, [alwaysFetch, passportTab, friendsData, effectiveToken, fetchFriends]);

  const handleCopyInvite = async () => {
    if (!friendsData?.referralCode) return;
    const link = `${window.location.origin}/loginclient?ref=${friendsData.referralCode}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      const el = document.createElement('textarea');
      el.value = link;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 2500);
  };

  const handleAcceptFriend = async (friendshipId: string) => {
    if (!effectiveToken) return;
    try {
      await axios.post(`${API_URL || ''}/api/friends/${friendshipId}/accept`, {}, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      await fetchFriends();
    } catch { /* */ }
  };

  const handleDeclineFriend = async (friendshipId: string) => {
    if (!effectiveToken) return;
    try {
      await axios.post(`${API_URL || ''}/api/friends/${friendshipId}/decline`, {}, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      await fetchFriends();
    } catch { /* */ }
  };

  const handleUnfriend = async (friendshipId: string) => {
    if (!effectiveToken) return;
    try {
      await axios.delete(`${API_URL || ''}/api/friends/${friendshipId}`, {
        headers: { Authorization: `Bearer ${effectiveToken}` },
      });
      await fetchFriends();
    } catch { /* */ }
  };

  return {
    friendsData,
    friendsLoading,
    inviteCopied,
    fetchFriends,
    handleCopyInvite,
    handleAcceptFriend,
    handleDeclineFriend,
    handleUnfriend,
  };
}
